import React, { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import AdminHeader from '../components/AdminHeader';
import { ReactComponent as EditSVG } from '../../../assets/images/edit.svg';
import Beads from '../components/Beads';
import { useDashboardQuery } from '../apis/dashboard';
import {
  compareParentsByGroupAndName,
  getParentGroupNames,
  isParentInGroup,
  isSameId,
} from '../apis/normalizeDashboardData';
import { hasFullFacilitatorProgramAccessRole } from '../../../utils/authRole';

const getFullName = ({ firstName, lastName }) =>
  [firstName, lastName].filter(Boolean).join(' ').trim();

const buildGroupDetail = (routeGroupId, dashboardData) => {
  if (!dashboardData) {
    return null;
  }

  const selectedGroup =
    dashboardData.groups.find((group) => group.groupId === routeGroupId) ?? null;

  if (!selectedGroup) {
    return null;
  }

  return {
    orgId: dashboardData.orgId,
    groupId: selectedGroup.groupId,
    groupName: selectedGroup.groupName,
    facilitators: selectedGroup.facilitators ?? [],
    parents: dashboardData.parents.filter(
      (parent) =>
        selectedGroup.parentIds.some((parentId) =>
          isSameId(parentId, parent.userId),
        ),
    ),
  };
};

const GroupInfo = () => {
  const { groupId } = useParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterGroup, setFilterGroup] = useState('all');
  const canEditGroups = hasFullFacilitatorProgramAccessRole(
    localStorage.getItem('role'),
  );
  const { data: dashboardData, isLoading, isError } = useDashboardQuery();
  const groupDetail = useMemo(
    () => buildGroupDetail(groupId, dashboardData),
    [dashboardData, groupId],
  );

  const filteredParents = useMemo(() => {
    if (!groupDetail) {
      return [];
    }

    return groupDetail.parents
      .filter((parent) => {
        const matchesGroup =
          filterGroup === 'all' || isParentInGroup(parent, filterGroup);
        const matchesSearch = getFullName(parent)
          .toLowerCase()
          .includes(searchTerm.toLowerCase());

        return matchesGroup && matchesSearch;
      })
      .sort((firstParent, secondParent) => {
        if (filterGroup === 'all') {
          return compareParentsByGroupAndName(firstParent, secondParent);
        }

        return getFullName(firstParent).localeCompare(
          getFullName(secondParent),
        );
      });
  }, [filterGroup, groupDetail, searchTerm]);

  if (isLoading) {
    return (
      <div className='text-left'>
        <AdminHeader />
        <main className='mx-auto max-w-5xl p-4'>
          <div className='my-3'>
            <Link
              className='inline-flex items-center justify-center rounded border border-light-blue bg-white px-3 py-1.5 text-sm font-bold text-light-blue no-underline transition-colors duration-200 hover:bg-light-blue hover:text-white'
              to='/facilitator/dashboard'
            >
              &larr; Go Back
            </Link>
          </div>
          <section className='rounded-lg border border-gray-200 bg-slate-50 p-4 shadow-sm'>
            <h2 className='text-xl font-bold text-dark-blue'>
              Loading group detail...
            </h2>
          </section>
        </main>
      </div>
    );
  }

  if (isError || !groupDetail) {
    return (
      <div className='text-left'>
        <AdminHeader />
        <main className='mx-auto max-w-5xl p-4'>
          <div className='my-3'>
            <Link
              className='inline-flex items-center justify-center rounded border border-light-blue bg-white px-3 py-1.5 text-sm font-bold text-light-blue no-underline transition-colors duration-200 hover:bg-light-blue hover:text-white'
              to='/facilitator/dashboard'
            >
              &larr; Go Back
            </Link>
          </div>
          <section className='rounded-lg border border-gray-200 bg-slate-50 p-4 shadow-sm'>
            <h2 className='text-xl font-bold text-red-600'>
              Unable to load group detail.
            </h2>
          </section>
        </main>
      </div>
    );
  }

  return (
    <div className='text-left'>
      <AdminHeader />
      <main className='relative mx-auto max-w-5xl p-4'>
        <div className='my-3'>
          <Link
            className='inline-flex items-center justify-center rounded border border-light-blue bg-white px-3 py-1.5 text-sm font-bold text-light-blue no-underline transition-colors duration-200 hover:bg-light-blue hover:text-white'
            to='/facilitator/dashboard'
          >
            &larr; Go Back
          </Link>
        </div>

        <h1 className='my-5 text-3xl font-bold text-dark-blue'>
          Group {groupDetail.groupName}
        </h1>

        <section className='rounded-lg border border-gray-200 bg-slate-50 p-4 shadow-sm'>
          <div className='mb-2 text-xs font-bold uppercase tracking-wide text-purple'>
            Group Info
          </div>
          <div className='flex flex-wrap items-start justify-between gap-4'>
            <div className='text-sm'>
              <div className='mb-1'>
                <span className='font-bold text-dark-blue'>Org ID:</span>{' '}
                {groupDetail.orgId}
              </div>
              <div className='mb-1'>
                <span className='font-bold text-dark-blue'>Group:</span>{' '}
                {groupDetail.groupName}
              </div>
              <div>
                <span className='font-bold text-dark-blue'>Facilitators:</span>{' '}
                {groupDetail.facilitators.length > 0
                  ? groupDetail.facilitators.map(getFullName).join(', ')
                  : 'None assigned'}
              </div>
            </div>
            {canEditGroups ? (
              <Link
                to={`/facilitator/dashboard/group/${groupDetail.groupId}/edit`}
                className='inline-flex min-h-[38px] items-center gap-2 rounded bg-light-blue px-3 py-1.5 text-sm font-bold text-white no-underline transition-colors duration-200 hover:bg-blue-600'
              >
                <EditSVG className='w-4 fill-current' />
                Edit Group Info
              </Link>
            ) : null}
          </div>
        </section>

        <section className='my-8 rounded-lg border border-gray-200 bg-slate-50 p-4 shadow-sm'>
          <div className='mb-4 flex flex-wrap items-center justify-between gap-3'>
            <h2 className='border-l-4 border-purple pl-3 text-xl font-bold text-dark-blue'>
              Parent Module Completion
            </h2>
            <div className='flex flex-wrap items-center gap-3'>
              <input
                type='text'
                placeholder='Search by name...'
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                className='rounded border border-gray-300 px-3 py-2 text-sm'
              />
              <select
                value={filterGroup}
                onChange={(event) => setFilterGroup(event.target.value)}
                className='rounded border border-gray-300 px-3 py-2 text-sm'
              >
                <option value='all'>All Groups</option>
                {dashboardData?.groups.map((group) => (
                  <option key={group.groupId} value={group.groupId}>
                    {group.groupName}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className='grid grid-cols-[22%_22%_56%] gap-4 rounded-t border border-gray-200 bg-white px-4 py-3.5 text-sm font-bold text-dark-blue'>
            <div>Parent Name</div>
            <div>Group</div>
            <div>Module Completion</div>
          </div>

          {filteredParents.map((parent) => {
            const parentGroupNames = getParentGroupNames(parent);

            return (
              <div
                key={parent.userId}
                className='grid grid-cols-[22%_22%_56%] gap-4 border-x border-b border-gray-200 bg-white px-4 py-4 text-sm items-start'
              >
                <div>
                  <Link
                    className='font-bold text-blue-600 hover:underline'
                    to={`/users/${parent.userId}`}
                  >
                    {getFullName(parent)}
                  </Link>
                </div>
                <div>{parentGroupNames.join(', ') || 'Unassigned'}</div>
                <div className='overflow-x-auto pb-1'>
                  <Beads statusArr={parent.modules} uniqueId={parent.userId} />
                </div>
              </div>
            );
          })}
        </section>
      </main>
    </div>
  );
};

export default GroupInfo;
